// English \u2014 canonical source of truth for the legal pages.
const privacy = {
  metaTitle: 'Privacy Policy for ISMS Calculator - GDPR & Data Privacy',
  metaDescription: 'ISMS Calculator Privacy Policy: discover what data we collect, how we use it, GDPR compliance, your data rights, and how we protect your information.',
  title: 'Privacy Policy',
  lastUpdated: 'Last updated: June 8, 2026',
  sections: [
    {
      heading: 'Data Controller',
      paragraphs: [
        'ISMS Calculator is a SaaS product operated by **I4U Services BV** (trading as ISMSCalculator), Luikersteenweg 543B, 3800 Sint-Truiden, Belgium \u2014 enterprise number 0897.752.915, VAT BE 0897.752.915 (hereinafter referred to as "we", "us", or "the Company").',
        'For any privacy-related inquiries, you may contact us at [support@ismscalculator.com](mailto:support@ismscalculator.com).',
      ],
    },
    {
      heading: 'What Data We Collect',
      paragraphs: ['When you use ISMS Calculator, we may collect the following information:'],
      bullets: [
        { term: 'Account data:', text: 'Your name, email address, and profile picture (when authenticating via Google OAuth).' },
        { term: 'Estimation data:', text: 'The calculator inputs you provide (company size, industry, maturity ratings, etc.) and the resulting estimates, when you choose to save them.' },
        { term: 'Assessment & lead data:', text: 'When you use our free mini-assessment or readiness self-check, we collect the email address you submit (and optional name and company), together with your answers and resulting score, so we can deliver your results and, where you have consented, send you relevant follow-up information.' },
        { term: 'Consultant introduction requests:', text: 'When you use the "Request an intro" feature in our Consultant Marketplace, we collect your name, email address, optional company name, and the message you write, in order to forward your request to the specific consultant you selected.' },
        { term: 'Technical data:', text: 'IP address, browser type, device information, and usage analytics collected automatically to maintain and improve the service.' },
      ],
    },
    {
      heading: 'How We Use Your Data',
      bullets: [
        'To provide and maintain the ISMS Calculator service.',
        'To authenticate your identity and manage your account.',
        'To store and retrieve your saved estimates.',
        'To deliver the results of any mini-assessment or readiness check you complete, and to manage leads you voluntarily submit.',
        'To facilitate introductions between you and independent consultants in our marketplace, when you explicitly request them.',
        'To improve the service, fix issues, and develop new features.',
        'To comply with legal obligations applicable under Belgian and EU law.',
      ],
    },
    {
      heading: 'Legal Basis for Processing (GDPR)',
      paragraphs: ['We process your personal data based on:'],
      bullets: [
        { term: 'Contractual necessity:', text: 'Processing required to provide the service you signed up for.' },
        { term: 'Legitimate interest:', text: 'Analytics and service improvement.' },
        { term: 'Consent:', text: 'Where explicitly given, such as marketplace introduction requests and optional marketing communications.' },
      ],
    },
    {
      heading: 'Data Storage and Security',
      paragraphs: ['Your data is stored on secure, encrypted servers. We implement industry-standard technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. Each user\u2019s saved estimates are fully isolated and not accessible to other users.'],
    },
    {
      heading: 'Data Retention',
      paragraphs: ['We retain your account data and saved estimates for as long as your account is active. You may delete individual estimates at any time. If you wish to delete your account entirely, contact us at the address above.'],
    },
    {
      heading: 'Your Rights (GDPR)',
      paragraphs: ['Under the General Data Protection Regulation (GDPR), you have the right to:'],
      bullets: [
        'Access the personal data we hold about you.',
        'Rectify inaccurate or incomplete data.',
        'Request erasure of your personal data.',
        'Restrict or object to certain processing activities.',
        'Data portability in a machine-readable format.',
        'Lodge a complaint with the Belgian Data Protection Authority (Gegevensbeschermingsautoriteit).',
      ],
    },
    {
      heading: 'Third-Party Services',
      paragraphs: ['We may use the following third-party services:'],
      bullets: [
        { term: 'Google OAuth:', text: 'For social login authentication. Google\u2019s privacy policy applies to their handling of your authentication data.' },
        { term: 'Email delivery (Resend):', text: 'To send transactional emails such as password resets, assessment reports, and consultant marketplace notifications.' },
        { term: 'Analytics providers:', text: 'To understand usage patterns and improve the service.' },
      ],
    },
    {
      heading: 'Consultant Marketplace & Data Sharing',
      paragraphs: ['Our Consultant Marketplace lets you request an introduction to independent, third-party ISO 27001 consultants listed in our directory. When you submit a "Request an intro":'],
      bullets: [
        'We share the details you provide \u2014 your name, email address, optional company name, and message \u2014 with the **specific consultant you selected**, so they can respond to you directly.',
        'Each consultant is an **independent third party and acts as a separate, independent data controller** for the information they receive. Once your request is forwarded, the consultant\u2019s own privacy practices govern how they use your data.',
        'We obtain your explicit consent (via a checkbox) before any introduction request is submitted, and we retain a record of the request to operate, secure, and improve the marketplace.',
        'We do not sell your personal data, and we do not share your marketplace request with consultants you did not choose to contact.',
      ],
    },
    {
      heading: 'Consultant Data-Processing Obligations',
      paragraphs: ['Consultants who receive your details through the marketplace are required, as a condition of being listed, to handle that data responsibly. In particular, each listed consultant agrees to:'],
      bullets: [
        'Use your information **solely to respond to and follow up on your introduction request**, and not for unrelated marketing without a separate lawful basis or your consent.',
        'Comply with the **EU General Data Protection Regulation (GDPR)** and all other applicable data-protection laws as an independent data controller.',
        'Keep your information **confidential and secure**, applying appropriate technical and organizational measures.',
        'Not sell your data or disclose it to further third parties without a lawful basis.',
        'Honour your data-subject rights \u2014 including access, correction, and erasure \u2014 and delete your details when they are no longer needed.',
      ],
    },
    {
      heading: 'Data Processing Addendum (DPA)',
      paragraphs: [
        'Where a consultant or service provider processes personal data **on our behalf** (as a processor) rather than as an independent controller, we put an appropriate **Data Processing Addendum (DPA)** in place in accordance with Article 28 GDPR. The DPA sets out the subject matter, duration, nature and purpose of the processing, the types of data and categories of data subjects, and the obligations and rights of the parties \u2014 including confidentiality, security, sub-processor controls, assistance with data-subject requests, and deletion or return of data at the end of the engagement.',
        'If you believe your data has been misused by a listed consultant, please contact us at [support@ismscalculator.com](mailto:support@ismscalculator.com) and we will investigate and, where appropriate, remove the listing.',
      ],
    },
    {
      heading: 'Cookies',
      paragraphs: ['We use essential cookies for authentication (session tokens). We may also use analytics cookies to understand how the service is used. You can control cookie settings through your browser preferences.'],
    },
    {
      heading: 'Changes to This Policy',
      paragraphs: ['We may update this Privacy Policy from time to time. We will notify you of significant changes by posting a notice on the service. Continued use of the service after changes constitutes acceptance of the revised policy.'],
    },
  ],
};

const terms = {
  metaTitle: 'Terms of Use for ISO 27001 Cost Estimator SaaS',
  metaDescription: 'Estimate ISO 27001 costs with ISMS Calculator. Real-time cost/effort, timelines, security maturity checks, model comparisons, and indicative estimates.',
  title: 'Terms of Use',
  lastUpdated: 'Last updated: June 8, 2026',
  sections: [
    {
      heading: 'Service Provider',
      paragraphs: ['ISMS Calculator is a SaaS product operated by **I4U Services BV** (trading as ISMSCalculator), Luikersteenweg 543B, 3800 Sint-Truiden, Belgium \u2014 enterprise number 0897.752.915, VAT BE 0897.752.915 (hereinafter referred to as "we", "us", or "the Company"). For inquiries, contact us at [support@ismscalculator.com](mailto:support@ismscalculator.com).'],
    },
    {
      heading: 'Acceptance of Terms',
      paragraphs: ['By accessing or using ISMS Calculator, you agree to be bound by these Terms of Use. If you do not agree with any part of these terms, you must not use the service.'],
    },
    {
      heading: 'Description of Service',
      paragraphs: ['ISMS Calculator provides a cost and effort estimation tool for organizations planning to implement ISO 27001 (Information Security Management System). The service includes:'],
      bullets: [
        'A real-time calculator for estimating implementation costs, effort, and timelines.',
        'Security maturity assessments across ISO 27001 domains.',
        'Model reference comparisons generated by our own cost model.',
        'ISO 27001 guidance documentation.',
        'A Consultant Marketplace directory where you can request introductions to independent, third-party ISO 27001 consultants.',
        'The ability to save and compare estimates (requires an account).',
      ],
    },
    {
      heading: 'Account Registration',
      paragraphs: ['The ISO 27001 cost calculator is free to use and requires no account. An account is only needed to save, name, compare, share or export your estimates, to run the AI-assisted readiness assessment, and to manage previously saved work. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.'],
    },
    {
      heading: 'Estimation Disclaimer',
      box: true,
      boxLabel: 'Important Notice:',
      paragraphs: [
        'The estimates, figures, model reference comparisons, and recommendations provided by ISMS Calculator are generated by our own published cost model in combination with the inputs you provide, external resources, and AI-assisted analysis. They are intended as **indicative ballpark figures only** and should not be treated as definitive quotes, guarantees, or professional advice.',
        'Actual implementation costs, timelines, and effort may vary significantly depending on your organization\u2019s specific circumstances, regulatory environment, existing controls, team capabilities, and many other factors that cannot be fully captured by an automated tool.',
        '**ISMS Calculator cannot be held liable or accountable for any decisions made, financial commitments entered into, or outcomes resulting from the use of the generated estimates or recommendations.** We strongly recommend engaging qualified ISO 27001 consultants and auditors for professional advice before making implementation decisions.',
      ],
    },
    {
      heading: 'AI and External Resources',
      paragraphs: ['ISMS Calculator uses algorithmic models and data derived from external resources, industry research, and AI-assisted analysis to generate its estimates and model reference comparisons. While we strive for accuracy and relevance, this data:'],
      bullets: [
        'May not reflect the most current market conditions or pricing.',
        'Is based on modelling assumptions rather than measured market averages, and may not apply to your specific situation.',
        'Should be independently verified before being used for budgeting or decision-making purposes.',
        'Does not constitute professional consulting, legal, or financial advice.',
      ],
    },
    {
      heading: 'Intellectual Property',
      paragraphs: ['All content, designs, algorithms, and code comprising the ISMS Calculator service are the intellectual property of ISMS Calculator. You may not copy, modify, distribute, or reverse-engineer any part of the service without prior written consent.'],
    },
    {
      heading: 'User Data Ownership',
      paragraphs: ['You retain ownership of the data you enter into the calculator and any saved estimates. We do not claim ownership over your input data or estimation results. Your data is isolated and not shared with other users. Where you choose to submit a Consultant Marketplace introduction request, the details you provide are shared with the consultant you selected, as described in our Privacy Policy.'],
    },
    {
      heading: 'Consultant Marketplace',
      paragraphs: ['ISMS Calculator offers a Consultant Marketplace that lists independent, third-party ISO 27001 consultants and lets you request an introduction to them. By using this feature you acknowledge that:'],
      bullets: [
        'Listed consultants are **independent third parties**. They are not our employees, agents, or partners, and we do not control their work.',
        'We provide the directory for convenience only. We do **not vet, certify, endorse, recommend, or guarantee** any consultant, their qualifications, availability, pricing, or the quality or outcome of their services.',
        'Any engagement, contract, fee arrangement, or advice is solely between you and the consultant. We are not a party to that relationship and accept **no liability** for it. You are responsible for conducting your own due diligence before engaging any consultant.',
        'Some listings may be marked "Sample" \u2014 these are illustrative placeholder entries and do not represent real, available consultants.',
        'When you submit an introduction request, you consent to your details being forwarded to the selected consultant, as set out in our Privacy Policy.',
      ],
    },
    {
      heading: 'Consultant Obligations & Data Processing',
      paragraphs: ['Consultants who are listed in the marketplace and who receive introduction requests agree, as a condition of being listed, to:'],
      bullets: [
        'Act as independent data controllers and process the personal data they receive in compliance with the **GDPR** and applicable law.',
        'Use the information **only to respond to the introduction request** and not for unsolicited marketing.',
        'Maintain appropriate confidentiality and security safeguards, and respect data-subject rights including erasure on request.',
        'Enter into a **Data Processing Addendum (DPA)** with us where they process personal data on our behalf, in accordance with Article 28 GDPR.',
      ],
    },
    {
      heading: 'Limitation of Liability',
      paragraphs: ['To the maximum extent permitted by Belgian and EU law:'],
      bullets: [
        'The service is provided "as is" without warranties of any kind, express or implied.',
        'ISMS Calculator shall not be liable for any direct, indirect, incidental, consequential, or special damages arising from your use of the service.',
        'We do not guarantee the accuracy, completeness, or reliability of any estimates or data generated by the tool.',
        'We are not liable for any engagement between you and a marketplace consultant, or for the acts or omissions of any consultant.',
        'Our total liability in any case shall not exceed the amount paid by you for the service in the 12 months preceding the claim.',
      ],
    },
    {
      heading: 'Termination',
      paragraphs: ['We may suspend or terminate your access to the service at our discretion if you violate these terms. You may stop using the service at any time. Upon termination, your right to access saved estimates will cease.'],
    },
    {
      heading: 'Governing Law',
      paragraphs: ['These Terms of Use are governed by and construed in accordance with the laws of Belgium. Any disputes arising from the use of this service shall be submitted to the exclusive jurisdiction of the courts of Hasselt, Belgium.'],
    },
    {
      heading: 'Changes to These Terms',
      paragraphs: ['We reserve the right to modify these Terms of Use at any time. Changes will be effective upon posting to the service. Your continued use of the service constitutes acceptance of the revised terms.'],
    },
  ],
};

const disclaimer = {
  metaTitle: 'Disclaimer for ISMS Calculator \u2014 ISO 27001 Estimation',
  metaDescription: 'ISMS Calculator: Free ISO 27001 cost & effort calculator with implementation roadmap, indicative estimates, Gantt timelines, PDF reports, and shareable links.',
  title: 'Disclaimer',
  lastUpdated: 'Last updated: June 8, 2026',
  topCallout: {
    title: 'Estimation Accuracy Disclaimer',
    paragraphs: [
      'ISMS Calculator provides **indicative cost and effort estimates** for ISO 27001 implementation planning. All figures, model reference comparisons, timelines, and recommendations generated by this tool are approximations produced by our own published cost model in combination with the inputs you provide and AI-assisted analysis. They are not observed market data.',
      '**These estimates are not professional advice. ISMS Calculator cannot be held liable or accountable for any decisions, financial commitments, or outcomes resulting from the use of the generated figures or estimations.**',
    ],
  },
  sections: [
    {
      heading: 'Nature of Estimates',
      paragraphs: ['The estimates provided by ISMS Calculator are:'],
      bullets: [
        { term: 'Ballpark figures only.', text: 'They represent a rough order of magnitude, not precise quotations.' },
        { term: 'Based on a generalized model.', text: 'Sector multipliers, maturity factors and model reference values are generated by our own calculation engine: they are modelling assumptions, not measured market averages, and may not reflect your specific organizational context.' },
        { term: 'Generated using algorithms and AI.', text: 'The calculation engine uses algorithmic models and data derived from external research and AI-assisted analysis. These models have inherent limitations and assumptions.' },
        { term: 'Subject to significant variability.', text: 'Actual costs can differ by 50% or more depending on organization-specific factors such as geographic location, existing security posture, regulatory requirements, team experience, vendor choices, and scope changes.' },
      ],
    },
    {
      heading: 'Not Professional Advice',
      paragraphs: ['ISMS Calculator does not provide professional consulting, legal, financial, or auditing advice. The tool is designed as a planning aid to help you understand the general scope and cost of an ISO 27001 implementation. It is **not a substitute** for engaging qualified:'],
      bullets: [
        'ISO 27001 consultants or lead auditors.',
        'Information security professionals familiar with your industry and regulatory environment.',
        'Financial advisors for budgeting and procurement decisions.',
        'Legal counsel for compliance and contractual matters.',
      ],
    },
    {
      heading: 'Model Reference Comparisons',
      paragraphs: ['The comparison values shown next to your estimate are **generated by the ISMSCalculator cost model itself**, by running the same engine against a representative organization profile for your sector and size. They are not survey data, market research or observed customer averages. These model reference values:'],
      bullets: [
        'Are modelling output, not empirical statistics: no percentile, pass-rate or market-average claim is made.',
        'Would in reality vary significantly by region, organization maturity, and specific regulatory requirements.',
        'Should be used as a relative orientation point only, never as a definitive industry standard.',
        'Are published in full, including every multiplier used, on our [Methodology](/en/methodology) page.',
      ],
    },
    {
      heading: 'Limitation of Liability',
      paragraphs: ['To the fullest extent permitted by Belgian and European Union law:'],
      bullets: [
        'ISMS Calculator is provided on an "as is" and "as available" basis.',
        'We make no warranties or representations, express or implied, regarding the accuracy, reliability, completeness, or fitness for purpose of any estimates or data.',
        'We shall not be liable for any loss, damage, cost, or expense (whether direct, indirect, incidental, or consequential) arising from reliance on the estimates, model reference comparisons, or recommendations provided by this tool.',
        'Users accept full responsibility for how they use the information provided.',
      ],
    },
    {
      heading: 'Recommendation',
      paragraphs: ['We strongly recommend that you:'],
      bullets: [
        'Use ISMS Calculator estimates as a starting point for discussions, not as final budgets.',
        'Engage certified ISO 27001 professionals to conduct a formal gap analysis and scoping exercise.',
        'Obtain multiple quotes from qualified implementation partners.',
        'Factor in organization-specific risks, compliance obligations, and resource constraints that the tool may not capture.',
      ],
    },
    {
      heading: 'Consultant Marketplace',
      paragraphs: [
        'The consultants listed in our Consultant Marketplace are **independent third parties**. ISMS Calculator does not vet, certify, endorse, recommend, or guarantee any listed consultant, their qualifications, availability, pricing, or the outcome of their services. Some listings may be sample or illustrative entries and do not represent real, available consultants.',
        'Any decision to contact or engage a consultant, and any resulting agreement, is made entirely at your own discretion and risk. We strongly recommend that you conduct your own due diligence \u2014 including verifying credentials, references, and certifications \u2014 before entering into any engagement. ISMS Calculator accepts no liability for any interaction with, advice from, or services provided by marketplace consultants.',
      ],
    },
    {
      heading: 'Company Information',
      paragraphs: ['This website and the ISMSCalculator service are published and operated by **I4U Services BV** (trading as ISMSCalculator), Luikersteenweg 543B, 3800 Sint-Truiden, Belgium \u2014 enterprise number 0897.752.915, VAT BE 0897.752.915. Contact: [support@ismscalculator.com](mailto:support@ismscalculator.com).'],
    },
    {
      heading: 'Contact',
      paragraphs: ['If you have questions about this disclaimer or the service, please contact us at [support@ismscalculator.com](mailto:support@ismscalculator.com).'],
    },
  ],
};

const en = { nav: { home: 'Back to Home' }, privacy, terms, disclaimer };
export default en;
